import React, { useEffect, useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Layout, StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { Button } from '../../../../components/form/button';
import { Field } from '../../../../components/field/field';
import { Typography } from '../../../../components/typography/typography';
import { User } from '../../../../clients/quipli-service/user/user';
import { StripeCard } from '../../../../clients/quipli-service/payment/payment';
import { Divider } from '../../../../components/divider/divider';
import { DateTime } from 'luxon';

export default function SetupPaymentForm({
  user,
  userCard,
  clientSecret,
  close,
  setCard,
  type,
  setupIntentId,
}: {
  user: User;
  userCard?: StripeCard;
  clientSecret: string;
  close?: () => void;
  setCard?: (stripeCard: StripeCard) => void;
  type: 'bankAccount' | 'cardPresent' | 'card';
  setupIntentId?: string;
}) {
  const { saveCardAdmin } = usePaymentApiV1();
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | undefined | null>(null);
  const [cardholderName, setCardholderName] = useState(userCard?.cardholderName || user.name);
  const [nickname, setNickname] = useState(userCard?.nickname || '');
  const [cardZip, setCardZip] = useState<string>(userCard?.zip || '');
  const [newExpirationMonth, setNewExpirationMonth] = useState<string>(userCard?.expirationMonth || '');
  const [newExpirationYear, setNewExpirationYear] = useState<string>(userCard?.expirationYear || '');

  const [paymentElementComplete, setPaymentElementComplete] = useState(false);
  const [submitState, setSubmitState] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (!clientSecret) {
      return;
    }
  }, []);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    setIsSubmitting(true);
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (type == 'card' && userCard) {
      await saveCardAdmin({
        paymentMethod: userCard.stripeCardId,
        cardholderName,
        nickname,
        userId: user.id,
        expirationMonth: newExpirationMonth,
        expirationYear: newExpirationYear,
        zip: cardZip,
      });
      setIsSubmitting(false);
      if (close) {
        close();
      }
      return;
    }

    try {
      const { error } = await elements.submit();
      if (error) {
        setMessage(error.message);
        return;
      }
    } catch (e: any) {
      setMessage(e.message);
      setIsSubmitting(false);
      return;
    }

    // @ts-expect-error not exposed in  stripe-js sdk
    const { error, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        payment_method_data: {
          billing_details: {
            name: cardholderName,
          },
        },
        return_url: `${window.location.origin}`,
      },
      redirect: 'if_required',
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error) {
      setIsSubmitting(false);
      if (error.type === 'card_error' || error.type === 'validation_error') {
        setMessage(error.message);
      } else {
        setMessage('An unexpected error occurred.');
      }
    }

    if (confirmationToken) {
      if (type == 'card') {
        //add fake card to state, capture setup or payment later with token
        const pmPreview = confirmationToken.payment_method_preview.card;
        const card = {
          paymentMethod: '',
          userId: user.id,
          stripeCardId: confirmationToken.id,
          cardholderName: cardholderName,
          brand: pmPreview.brand,
          expirationMonth: String(pmPreview.exp_month).padStart(2, '0'),
          expirationYear: pmPreview.exp_year.toString().slice(-2),
          last4: pmPreview.last4,
          nickname: nickname,
          createdAt: DateTime.now(),
          updatedAt: DateTime.now(),
          confirmationTokenId: confirmationToken.id,
          setupIntentId: setupIntentId,
        };
        const shellCard = await saveCardAdmin(card);
        setCard && setCard({ ...shellCard, confirmationTokenId: confirmationToken.id });
      }
    } else {
      throw new Error('No setup intent returned');
    }
    setIsSubmitting(false);
    document.body.dispatchEvent(new CustomEvent('admin-payment-setup-complete'));
    if (close) {
      close();
    }
  };

  const cancelButton = (
    <>
      <Button
        className="button-secondary"
        onClick={() => {
          document.body.dispatchEvent(new CustomEvent('admin-payment-setup-complete'));
          if (close) {
            close();
          }
        }}
      >
        Cancel
      </Button>
    </>
  );

  function checkPaymentElement(event: StripePaymentElementChangeEvent) {
    if (event.complete) {
      setPaymentElementComplete(true);
    }
    toggleSubmit();
  }

  function toggleSubmit() {
    setSubmitState(!(paymentElementComplete && cardholderName != ''));
  }

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: { type: 'tabs' as Layout },
    terms: { card: 'never' },
    defaultValues: { billingDetails: { email: user.email } },
  };

  useEffect(() => {
    if (userCard) {
      const regexExpYear = new RegExp('^[2][0-9]{3}$');
      const regexExpMonth = new RegExp('^(((0)[0-9])|((1)[0-2]))$');

      setSubmitState(
        !(cardholderName != '' && regexExpMonth.test(newExpirationMonth) && regexExpYear.test(newExpirationYear)),
      );
    } else {
      toggleSubmit();
    }
  }, [paymentElementComplete, cardholderName, newExpirationMonth, newExpirationYear, cardZip]);

  return (
    <form id="payment-form">
      {type == 'card' && (
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <Field
            className="grow"
            type="text"
            label="Card nick name"
            labelType="text-xs"
            labelWeight="medium"
            value={nickname}
            onChange={(value: string) => setNickname(value)}
            placeholder="e-g Adam Visa"
          />

          <Field
            className="grow"
            type="text"
            label="Card holder name"
            labelType="text-xs"
            labelWeight="medium"
            value={cardholderName}
            onChange={(value: string) => setCardholderName(value)}
            placeholder="e-g Adam Smith"
          />
        </div>
      )}

      {!userCard && (
        <PaymentElement id="payment-element" options={paymentElementOptions} onChange={checkPaymentElement} />
      )}
      {userCard && (
        <>
          <Field
            type="text"
            label="Zip/Postal Code"
            labelType="text-sm"
            value={cardZip}
            onChange={(value) => {
              setCardZip(value);
            }}
            id="card-zip-code"
          ></Field>
          <Field
            type="text"
            label="Exp Month"
            labelType="text-sm"
            value={newExpirationMonth}
            onChange={(value) => {
              setNewExpirationMonth(value);
            }}
            id="card-expiration-month"
          ></Field>
          <Field
            type="text"
            label="Exp Year"
            labelType="text-sm"
            value={newExpirationYear}
            onChange={(value) => {
              setNewExpirationYear(value);
            }}
            id="card-expiration-year"
          ></Field>
        </>
      )}

      <div className="mt-4">
        <Divider />
      </div>

      <div className="flex justify-start gap-2 p-3">
        {cancelButton}
        <Button className="primary" disabled={submitState || isSubmitting} onClick={handleSubmit}>
          Save Payment
        </Button>
      </div>
      {message && (
        <Typography className={'error'} type={'text-md'}>
          {message}
        </Typography>
      )}
    </form>
  );
}
